/**
 * Teams tab displaying a reorderable table with management actions for
 * creating, editing, and deleting teams within an account.
 */
import { ChevronDown, ChevronUp, Pencil, Plus, Trash2, Users } from "lucide-react";
import { useState } from "react";

import { CreateTeamDialog } from "./CreateTeamDialog";
import { DeleteTeamDialog } from "./DeleteTeamDialog";
import { EditTeamDialog } from "./EditTeamDialog";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { useAuthStore } from "@/stores/auth";


interface Team {
	id: string;
	name: string;
	order: number;
	settings: {
		timezone?: string;
		dateFormat?: string;
		currency?: string;
	} | null;
	createdAt: Date;
	account: {
		id: string;
		name: string;
		slug: string;
	} | null;
}

/**
 * Teams tab component for managing teams within an account
 */
export function TeamsTab() {
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
	const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);

	const { teams: authTeams, setTeams } = useAuthStore();

	// Fetch teams
	const teamsQuery = trpc.teams.list.useQuery();

	// Reorder mutation
	const reorderMutation = trpc.teams.reorder.useMutation({
		onSuccess: async () => {
			const result = await teamsQuery.refetch();
			if (result.data) {
				updateAuthStore(result.data);
			}
		},
	});

	/**
	 * Updates the auth store with new teams data
	 * @param newTeams - teams data from the API
	 */
	const updateAuthStore = (newTeams: typeof teamsQuery.data) => {
		if (!newTeams) return;
		// Merge new teams with existing teams from other accounts
		const otherAccountTeams = authTeams.filter(
			(t) => !newTeams.some((newT) => newT.account?.id === t.account.id)
		);
		const updatedTeams = [
			...otherAccountTeams,
			...newTeams.map((t) => ({
				...t,
				account: t.account ?? { id: "", name: "", slug: "" },
			})),
		];
		setTeams(updatedTeams);
	};

	/** Callback when team is created - refetches list, updates auth store, and closes dialog */
	const handleTeamCreated = async () => {
		const result = await teamsQuery.refetch();
		if (result.data) {
			updateAuthStore(result.data);
		}
		setIsCreateDialogOpen(false);
	};

	/** Callback when team is updated - refetches list, updates auth store, and closes dialog */
	const handleTeamUpdated = async () => {
		const result = await teamsQuery.refetch();
		if (result.data) {
			updateAuthStore(result.data);
		}
		setSelectedTeam(null);
	};

	/** Callback when team is deleted - refetches list, updates auth store, and closes dialog */
	const handleTeamDeleted = async () => {
		const result = await teamsQuery.refetch();
		if (result.data) {
			updateAuthStore(result.data);
		}
		setTeamToDelete(null);
	};

	/**
	 * Move team up in order (swap with previous team)
	 * @param team - team to move up
	 * @param index - current index in the list
	 */
	const handleMoveUp = (team: Team, index: number) => {
		if (!teamsQuery.data || index === 0) return;

		const prevTeam = teamsQuery.data[index - 1];
		if (!prevTeam) return;

		// Swap orders
		reorderMutation.mutate({
			teamOrders: [
				{ teamId: team.id, order: prevTeam.order },
				{ teamId: prevTeam.id, order: team.order },
			],
		});
	};

	/**
	 * Move team down in order (swap with next team)
	 * @param team - team to move down
	 * @param index - current index in the list
	 */
	const handleMoveDown = (team: Team, index: number) => {
		if (!teamsQuery.data || index === teamsQuery.data.length - 1) return;

		const nextTeam = teamsQuery.data[index + 1];
		if (!nextTeam) return;

		// Swap orders
		reorderMutation.mutate({
			teamOrders: [
				{ teamId: team.id, order: nextTeam.order },
				{ teamId: nextTeam.id, order: team.order },
			],
		});
	};

	/**
	 * Formats team settings for display
	 * @param settings - team settings object
	 */
	const formatSettings = (settings: Team["settings"]) => {
		if (!settings) return "No custom settings";
		const parts: string[] = [];
		if (settings.timezone) parts.push(settings.timezone);
		if (settings.currency) parts.push(settings.currency);
		if (settings.dateFormat) parts.push(settings.dateFormat);
		return parts.length > 0 ? parts.join(", ") : "No custom settings";
	};

	return (
		<div className="space-y-4">
			{/* Header */}
			<div className="flex items-center justify-between">
				<p className="text-sm text-muted-foreground">
					Manage teams within your account. Use arrows to reorder.
				</p>
				<Button onClick={() => { setIsCreateDialogOpen(true); }}>
					<Plus className="mr-2 h-4 w-4" />
					Create Team
				</Button>
			</div>

			{/* Teams Table */}
			{teamsQuery.isLoading ? (
				<div className="flex items-center justify-center py-8">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
				</div>
			) : teamsQuery.data?.length === 0 ? (
				<Card>
					<CardContent className="flex flex-col items-center justify-center py-12">
						<Users className="h-12 w-12 text-muted-foreground mb-4" />
						<h3 className="text-lg font-medium mb-2">No Teams</h3>
						<p className="text-muted-foreground text-center max-w-md mb-4">
							Create teams to organize users and manage access to different
							parts of your account.
						</p>
						<Button onClick={() => { setIsCreateDialogOpen(true); }}>
							<Plus className="mr-2 h-4 w-4" />
							Create First Team
						</Button>
					</CardContent>
				</Card>
			) : (
				<Card>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-[80px]">Order</TableHead>
								<TableHead>Name</TableHead>
								<TableHead>Settings</TableHead>
								<TableHead className="w-[100px]">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{teamsQuery.data?.map((team, index) => (
								<TableRow key={team.id}>
									<TableCell>
										<div className="flex items-center gap-1">
											<Button
												variant="ghost"
												size="icon"
												className="h-6 w-6"
												disabled={index === 0 || reorderMutation.isPending}
												onClick={() => { handleMoveUp(team, index); }}
												title="Move up"
											>
												<ChevronUp className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="h-6 w-6"
												disabled={
													index === teamsQuery.data.length - 1 ||
													reorderMutation.isPending
												}
												onClick={() => { handleMoveDown(team, index); }}
												title="Move down"
											>
												<ChevronDown className="h-4 w-4" />
											</Button>
										</div>
									</TableCell>
									<TableCell className="font-medium">{team.name}</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{formatSettings(team.settings)}
									</TableCell>
									<TableCell>
										<div className="flex items-center gap-1">
											<Button
												variant="ghost"
												size="icon"
												onClick={() => { setSelectedTeam(team); }}
												title="Edit team"
											>
												<Pencil className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												onClick={() => { setTeamToDelete(team); }}
												title="Delete team"
											>
												<Trash2 className="h-4 w-4 text-red-400" />
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Card>
			)}

			{/* Create Team Dialog */}
			<CreateTeamDialog
				open={isCreateDialogOpen}
				onOpenChange={setIsCreateDialogOpen}
				onSuccess={handleTeamCreated}
			/>

			{/* Edit Team Dialog */}
			{selectedTeam && (
				<EditTeamDialog
					team={selectedTeam}
					open={!!selectedTeam}
					onOpenChange={(open) => { if (!open) setSelectedTeam(null); }}
					onSuccess={handleTeamUpdated}
				/>
			)}

			{/* Delete Team Dialog */}
			{teamToDelete && (
				<DeleteTeamDialog
					team={teamToDelete}
					open={!!teamToDelete}
					onOpenChange={(open) => { if (!open) setTeamToDelete(null); }}
					onSuccess={handleTeamDeleted}
				/>
			)}
		</div>
	);
}
